// Canonical kinds for the email-preference domain.
//
// Single source of truth for the `reportType` and `scopeType` string
// constants used across the React/Server-Action layer. Mirrored by
// `crawler/ticker_data_crawler/pipelines/emails/common/kinds.py`.
//
// The Prisma DB columns (EmailPreference.reportType / scopeType) remain
// TEXT — this module is the runtime + static safety net so callers
// cannot drift on typos. Mirror this file against the Python counterpart
// — both must agree.

export const REPORT_TYPES = {
  PORTFOLIO_FUND_REPORTS: "portfolio_fund_reports",
  PORTFOLIO_DY_UPDATES: "portfolio_dy_updates",
  PORTFOLIO_DIVIDEND_MONTHLY: "portfolio_dividend_monthly",
  FUND_REPORT: "fund_report",
  CAMPAIGN: "campaign",
  AI_REPORT: "ai_report",
} as const;

export type ReportType = (typeof REPORT_TYPES)[keyof typeof REPORT_TYPES];

export const SCOPE_TYPES = {
  GLOBAL: "global",
  PORTFOLIO: "portfolio",
  FUND: "fund",
} as const;

export type ScopeType = (typeof SCOPE_TYPES)[keyof typeof SCOPE_TYPES];

export const CAMPAIGN_SCOPE_ID = "*" as const;

// Default cadence (days) per report type. Mirrored by
// `DEFAULT_FREQUENCY_DAYS_BY_REPORT_TYPE` in
// `crawler/ticker_data_crawler/pipelines/emails/common/kinds.py`.
// Keep the two in sync.
export const DEFAULT_FREQUENCY_DAYS_BY_REPORT_TYPE: Partial<
  Record<ReportType, number>
> = {
  [REPORT_TYPES.PORTFOLIO_FUND_REPORTS]: 15,
  [REPORT_TYPES.PORTFOLIO_DY_UPDATES]: 7,
  [REPORT_TYPES.PORTFOLIO_DIVIDEND_MONTHLY]: 30,
  [REPORT_TYPES.FUND_REPORT]: 15,
  // REPORT_TYPES.CAMPAIGN is excluded: campaign cadence is driven by
  // the campaign row's `scheduled_at`, not by the preference's
  // `frequency_days`. If a missing key is queried, FALLBACK applies.
};

export const FALLBACK_FREQUENCY_DAYS = 30;

export const getDefaultFrequencyDays = (reportType: string): number =>
  DEFAULT_FREQUENCY_DAYS_BY_REPORT_TYPE[reportType as ReportType] ??
  FALLBACK_FREQUENCY_DAYS;
